import React, { useContext, useMemo } from 'react';
import { PieChart } from '@mui/x-charts/PieChart';
import { Box, CircularProgress, Typography, Card, CardHeader, CardContent, Stack } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { CompliantVsNonCompliantMetricsResponse } from '../_types';
import { AppContext } from 'views/App/context';
import useTrainingComplianceSummary from '../_data_sources/useTrainingComplianceSummary';
import { primary, neutral } from '_lex/_foundations/phxColors';

const complianceColors = [primary[300], primary[700]];

interface LegendItemProps {
  color: string;
  label: string;
  value: string;
}

function LegendItem({ color, label, value }: LegendItemProps) {
  return (
    <Box
      sx={{
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
        gap: 2,
        display: 'inline-flex',
      }}
    >
      <Box
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: 1,
          display: 'inline-flex',
        }}
      >
        <Box
          sx={{
            width: 16,
            height: 16,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              left: 4,
              top: 4,
              position: 'absolute',
              background: color,
              borderRadius: '50%',
            }}
          />
        </Box>
        <Box
          sx={{
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            gap: 1,
            display: 'inline-flex',
          }}
        >
          <Typography
            sx={{
              color: neutral[500],
              fontSize: 12,
              fontFamily: 'Inter',
              fontWeight: 400,
              lineHeight: '16.8px',
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{
          alignSelf: 'stretch',
          textAlign: 'center',
          color: neutral[700],
          fontSize: 14,
          fontFamily: 'Inter',
          fontWeight: 500,
          lineHeight: '21px',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export function ComplianceSummary() {
  const { currentOrganizationId } = useContext(AppContext);
  const compliantQuery = useTrainingComplianceSummary(currentOrganizationId);

  const { data, isLoading } = compliantQuery;

  // Calculate compliance percentages from API response
  const complianceData = useMemo(() => {
    if (!data || !data.total || data.total === 0) {
      return [
        { id: 0, value: 0, label: t('reporting.insightsCenter.complianceDonutChart.compliant'), count: 0 },
        {
          id: 1,
          value: 0,
          label: t('reporting.insightsCenter.complianceDonutChart.nonCompliant'),
          count: 0,
        },
      ];
    }

    const compliant = Math.round((data.compliant / data.total) * 100);
    const nonCompliant = Math.round((data.nonCompliant / data.total) * 100);

    return [
      {
        id: 0,
        value: compliant,
        label: t('reporting.insightsCenter.complianceDonutChart.compliant'),
        count: data.compliant,
      },
      {
        id: 1,
        value: nonCompliant,
        label: t('reporting.insightsCenter.complianceDonutChart.nonCompliant'),
        count: data.nonCompliant,
      },
    ];
  }, [data]);

  const isEmpty = useMemo(() => {
    return !data || !data.total || data.total === 0 || (data.compliant === 0 && data.nonCompliant === 0);
  }, [data]);

  if (isLoading) {
    return (
      <Card className='lex-chart-card-white'>
        <CardHeader title={t('reporting.insightsCenter.complianceDonutChart.title')} />
        <CardContent>
          <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (isEmpty) {
    return (
      <Card className='lex-chart-card-white'>
        <CardHeader title={t('reporting.insightsCenter.complianceDonutChart.title')} />
        <CardContent>
          <Box
            sx={{
              width: '100%',
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Stack
              spacing={2}
              sx={{
                textAlign: 'center',
                alignItems: 'center',
              }}
            >
              <Typography
                variant='subtitle1'
                sx={{
                  color: neutral[500],
                }}
              >
                {t('reporting.insightsCenter.complianceDonutChart.emptyState')}
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='lex-chart-card-white'>
      <CardHeader title={t('reporting.insightsCenter.complianceDonutChart.title')} />
      <CardContent>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <PieChart
            colors={complianceColors}
            series={[
              {
                data: complianceData,
                valueFormatter: (item) => {
                  const count = (item as (typeof complianceData)[0]).count ?? 0;
                  return `${item.value}% (${count.toLocaleString()})`;
                },
                innerRadius: 60,
                outerRadius: 100,
                paddingAngle: 0,
                cornerRadius: 0,
                startAngle: 0,
                endAngle: 360,
              },
            ]}
            margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
            height={200}
            slotProps={{
              legend: { hidden: true },
            }}
          />
          <Box
            sx={{
              alignSelf: 'stretch',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 8,
              display: 'inline-flex',
              pt: 10,
            }}
          >
            <Box
              sx={{
                flex: '1 1 0',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 5,
                display: 'flex',
              }}
            >
              <LegendItem
                color={primary[300]}
                label={t('reporting.insightsCenter.complianceDonutChart.compliant')}
                value={`${complianceData[0]?.value ?? 0}%`}
              />
              <Box sx={{ width: 110 }}>
                <LegendItem
                  color={primary[700]}
                  label={t('reporting.insightsCenter.complianceDonutChart.nonCompliant')}
                  value={`${complianceData[1]?.value ?? 0}%`}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default ComplianceSummary;
